import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Custom hook for WebSocket connection with auto-reconnect
 * 
 * @param {string} token - JWT authentication token
 * @param {object} handlers - Event handlers for different message types
 * @param {boolean} enabled - Whether WebSocket should be connected
 * @returns {object} - Connection state and methods
 */
export const useWebSocket = (token, handlers = {}, enabled = true) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const missedPongsRef = useRef(0);  // Dead-man's switch counter
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000; // 1 second
  const maxMissedPongs = 3;  // Trigger reconnect after 3 missed pongs

  // Get WebSocket URL from current location
  const getWebSocketUrl = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/api/ws?token=${token}`;
  }, [token]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!token || !enabled) return;
    
    // Don't connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN || 
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    try {
      const wsUrl = getWebSocketUrl();
      void 0 && console.log('[WebSocket] Connecting...');
      
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        void 0 && console.log('[WebSocket] Connected');
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;
        missedPongsRef.current = 0;  // Reset missed pongs on fresh connection

        // Start ping interval to keep connection alive (dead-man's switch)
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            // Step 1: Increment missed pongs counter before sending ping
            missedPongsRef.current++;
            
            // Step 3: If too many pongs missed, connection is zombie - force reconnect
            if (missedPongsRef.current >= maxMissedPongs) {
              console.warn(`[WebSocket] ${maxMissedPongs} pongs missed - zombie connection detected, forcing reconnect`);
              wsRef.current.close(4000, 'Zombie connection - missed pongs');
              return;
            }
            
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 10000); // Ping every 10 seconds to prevent proxy/ingress idle timeout
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          void 0 && console.log('[WebSocket] Received:', data.type);

          // Handle different message types
          switch (data.type) {
            case 'connected':
              void 0 && console.log('[WebSocket] Connection confirmed:', data.message);
              break;
            case 'pong':
              // Step 2: Reset missed pongs counter on successful pong
              missedPongsRef.current = 0;
              break;
            case 'new_message':
              if (handlers.onNewMessage) {
                handlers.onNewMessage(data.thread_id, data.message);
              }
              break;
            case 'message_thread_update':
              if (handlers.onThreadUpdate) {
                handlers.onThreadUpdate(data.thread_id, data.thread);
              }
              break;
            case 'notification':
              if (handlers.onNotification) {
                handlers.onNotification(data.notification);
              }
              break;
            case 'appointment_update':
              if (handlers.onAppointmentUpdate) {
                handlers.onAppointmentUpdate(data.action, data.appointment);
              }
              break;
            case 'task_update':
              if (handlers.onTaskUpdate) {
                handlers.onTaskUpdate(data.action, data.task);
              }
              break;
            case 'urgent_waitlist_cleared':
              if (handlers.onUrgentWaitlistCleared) {
                handlers.onUrgentWaitlistCleared(data.client_id);
              }
              break;
            case 'staff_announcement':
              if (handlers.onStaffAnnouncement) {
                handlers.onStaffAnnouncement(data.announcement);
              }
              break;
            default:
              void 0 && console.log('[WebSocket] Unknown message type:', data.type);
          }
        } catch (e) {
          console.error('[WebSocket] Failed to parse message:', e);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        setConnectionError('WebSocket connection error');
      };

      wsRef.current.onclose = (event) => {
        void 0 && console.log('[WebSocket] Closed:', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Attempt to reconnect with exponential backoff
        if (enabled && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(
            baseReconnectDelay * Math.pow(2, reconnectAttempts.current),
            30000 // Max 30 seconds
          );
          void 0 && console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          setConnectionError('Max reconnection attempts reached');
        }
      };
    } catch (error) {
      console.error('[WebSocket] Failed to create connection:', error);
      setConnectionError(error.message);
    }
  }, [token, enabled, getWebSocketUrl, handlers]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (enabled && token) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, token, connect, disconnect]);

  // Reconnect when handlers change (they're passed as dependency)
  useEffect(() => {
    if (isConnected && wsRef.current) {
      // Update handlers without reconnecting
      // The current implementation reads handlers in onmessage, so this is fine
    }
  }, [handlers, isConnected]);

  return {
    isConnected,
    connectionError,
    reconnect: connect,
    disconnect
  };
};

export default useWebSocket;
